<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    modal
    :closable="!busy"
    style="width: 640px; max-width: 95vw"
  >
    <template #header>
      <h3 style="margin: 0">Ansprechpartner zuweisen</h3>
    </template>
    <div v-if="team" style="margin-bottom: 12px">
      Mannschaft: <strong>{{ team.name || team.representation }}</strong>
    </div>
    <h4 style="color: red" v-if="error && error.length > 0">{{ error }}</h4>
    <div class="field grid">
      <label class="col-fixed" style="width: 140px">Rolle</label>
      <div class="col">
        <Dropdown
          v-model="selectedRole"
          :options="roles"
          optionLabel="label"
          optionValue="value"
          style="width: 100%"
        />
        <small v-if="currentContactPersonName">
          Aktuell: {{ currentContactPersonName }} – wird ersetzt.
        </small>
      </div>
    </div>
    <div class="field grid">
      <label class="col-fixed" style="width: 140px">Auswahl</label>
      <div class="col">
        <SelectButton
          v-model="mode"
          :options="modes"
          optionLabel="label"
          optionValue="value"
          @change="onModeChanged"
        />
      </div>
    </div>
    <div v-if="mode === 'existing'" class="field grid">
      <label class="col-fixed" style="width: 140px">Ansprechpartner</label>
      <div class="col">
        <Dropdown
          v-model="selectedContactPerson"
          :options="contactPersons"
          optionLabel="representation"
          dataKey="id"
          placeholder="Ansprechpartner auswählen"
          :filter="true"
          style="width: 100%"
        />
      </div>
    </div>
    <div v-else>
      <div class="field grid">
        <label class="col-fixed" style="width: 140px">Name</label>
        <div class="col">
          <InputText
            v-model="newContactPerson.name"
            style="width: 100%"
            @blur="checkForDuplicates()"
          />
        </div>
      </div>
      <div class="field grid">
        <label class="col-fixed" style="width: 140px">E-Mail</label>
        <div class="col">
          <InputText
            v-model="newContactPerson.email"
            style="width: 100%"
            @blur="checkForDuplicates()"
          />
        </div>
      </div>
      <div class="field grid">
        <label class="col-fixed" style="width: 140px">Telefonnummer</label>
        <div class="col">
          <InputText
            v-model="newContactPerson.phoneNumber"
            style="width: 100%"
            @blur="checkForDuplicates()"
          />
        </div>
      </div>
    </div>
    <div v-if="mode === 'new' && duplicates.length > 0" class="duplicate-hint">
      <div class="duplicate-hint-header">
        <i class="pi pi-exclamation-triangle"></i>
        Diesen Ansprechpartner gibt es möglicherweise schon:
      </div>
      <div v-for="duplicate in duplicates" :key="duplicate.id" class="duplicate">
        <div class="duplicate-name">
          {{ duplicate.name }}
          <span class="duplicate-score">{{ duplicate.score }} %</span>
        </div>
        <div class="duplicate-details">{{ details(duplicate) }}</div>
        <div class="duplicate-reasons">{{ duplicate.reasons.join(", ") }}</div>
        <Button
          label="Diesen zuweisen"
          icon="pi pi-user-plus"
          class="p-button-sm"
          :disabled="busy"
          @click="assignExisting(duplicate.id)"
        />
      </div>
    </div>
    <template #footer>
      <Button
        label="Abbrechen"
        severity="secondary"
        icon="pi pi-times"
        :disabled="busy"
        @click="$emit('update:visible', false)"
      />
      <Button
        v-if="mode === 'new' && duplicates.length > 0"
        label="Trotzdem neu anlegen"
        severity="warning"
        icon="pi pi-user-plus"
        :disabled="busy || !newContactPerson.name"
        :loading="busy"
        @click="assign(true)"
      />
      <Button
        v-else
        label="Zuweisen"
        icon="pi pi-check"
        :disabled="!assignable || busy"
        :loading="busy"
        @click="assign(false)"
      />
    </template>
  </Dialog>
</template>

<script>
import ServerConnection from "../../services/ServerConnection/ServerConnection";
import {
  loadContactPersonDuplicates,
  contactPersonDuplicateDetails,
} from "../../services/ContactPersonService/ContactPersonDuplicates";
import Dialog from "primevue/dialog";
import Dropdown from "primevue/dropdown";
import SelectButton from "primevue/selectbutton";
import InputText from "primevue/inputtext";
import Button from "primevue/button";

export default {
  name: "AssignContactPersonDialog",
  components: { Dialog, Dropdown, SelectButton, InputText, Button },
  props: {
    visible: { type: Boolean, default: false },
    team: { type: Object, default: null },
  },
  emits: ["update:visible", "assigned"],
  data() {
    return {
      roles: [
        { label: "Ansprechpartner/Kapitän", value: "PRIMARY" },
        { label: "Stellv. Ansprechpartner", value: "SECONDARY" },
      ],
      modes: [
        { label: "Vorhandener", value: "existing" },
        { label: "Neu anlegen", value: "new" },
      ],
      selectedRole: "PRIMARY",
      mode: "existing",
      contactPersons: [],
      selectedContactPerson: null,
      newContactPerson: { name: "", email: "", phoneNumber: "" },
      duplicates: [],
      busy: false,
      error: "",
    };
  },
  computed: {
    currentContactPersonName() {
      if (!this.team) {
        return "";
      }
      var contactPerson =
        this.selectedRole === "PRIMARY" ? this.team.primaryContact : this.team.secondaryContact;
      if (!contactPerson) {
        return "";
      }
      return contactPerson.representation || contactPerson.name || "";
    },
    assignable() {
      if (this.mode === "existing") {
        return this.selectedContactPerson && this.selectedContactPerson.id;
      }
      return this.newContactPerson.name && this.newContactPerson.name.trim().length > 0;
    },
  },
  watch: {
    visible(value) {
      if (value) {
        this.openDialog();
      }
    },
  },
  methods: {
    openDialog() {
      this.error = "";
      this.busy = false;
      this.mode = "existing";
      this.selectedRole = "PRIMARY";
      this.selectedContactPerson = null;
      this.newContactPerson = { name: "", email: "", phoneNumber: "" };
      this.duplicates = [];
      this.loadContactPersons();
    },
    loadContactPersons() {
      ServerConnection.get("data/contactperson")
        .then((response) => {
          var list = Array.isArray(response.data) ? response.data : [];
          list.sort((a, b) =>
            (a.representation || a.name || "").localeCompare(b.representation || b.name || "")
          );
          this.contactPersons = list;
        })
        .catch((err) => {
          this.error = "Konnte Ansprechpartner nicht laden: " + this.errorMessage(err);
        });
    },
    onModeChanged() {
      this.error = "";
      this.duplicates = [];
      if (this.mode === "new") {
        this.checkForDuplicates();
      }
    },
    details(duplicate) {
      return contactPersonDuplicateDetails(duplicate);
    },
    checkForDuplicates() {
      if (this.mode !== "new" || !this.newContactPerson.name) {
        return;
      }
      loadContactPersonDuplicates(this.newContactPerson)
        .then((duplicates) => {
          this.duplicates = duplicates;
        })
        .catch(() => {
          this.duplicates = [];
        });
    },
    assignExisting(contactPersonId) {
      this.sendAssignment({ role: this.selectedRole, contactPersonId: contactPersonId });
    },
    assign(ignoreDuplicates) {
      if (this.mode === "existing") {
        this.assignExisting(this.selectedContactPerson.id);
        return;
      }
      this.sendAssignment({
        role: this.selectedRole,
        name: this.newContactPerson.name,
        email: this.newContactPerson.email,
        phoneNumber: this.newContactPerson.phoneNumber,
        ignoreDuplicates: ignoreDuplicates === true,
      });
    },
    sendAssignment(assignment) {
      if (!this.team || !this.team.id) {
        return;
      }
      this.error = "";
      this.busy = true;
      ServerConnection.put(
        "team/" + this.team.id + "/contactperson",
        JSON.stringify(assignment)
      )
        .then((response) => {
          this.busy = false;
          this.$emit("assigned", { ...response.data, role: this.selectedRole });
          this.$emit("update:visible", false);
        })
        .catch((err) => {
          this.busy = false;
          if (err && err.response && err.response.status === 409) {
            this.duplicates = err.response.data.duplicates || [];
            this.error =
              "Es gibt bereits einen Ansprechpartner mit diesen Angaben. Bitte vorhandenen zuweisen oder trotzdem neu anlegen.";
            return;
          }
          this.error = "Zuweisen fehlgeschlagen: " + this.errorMessage(err);
        });
    },
    errorMessage(err) {
      if (err && err.response && err.response.data) {
        return typeof err.response.data === "string"
          ? err.response.data
          : JSON.stringify(err.response.data);
      }
      if (err && err.message) {
        return err.message;
      }
      return "Unbekannter Fehler";
    },
  },
};
</script>

<style scoped>
.duplicate-hint {
  margin-top: 12px;
  padding: 10px;
  border: 1px solid #f0c36d;
  border-radius: 4px;
  background-color: #fdf6e3;
}
.duplicate-hint-header {
  font-weight: bold;
  margin-bottom: 8px;
}
.duplicate {
  padding: 6px 0;
  border-top: 1px solid #f0e0b0;
}
.duplicate:first-of-type {
  border-top: none;
}
.duplicate-name {
  font-weight: bold;
}
.duplicate-score {
  font-weight: normal;
  color: #7a6a3a;
}
.duplicate-details,
.duplicate-reasons {
  font-size: 0.9em;
  color: #6b6b6b;
  margin-bottom: 4px;
}
</style>
